import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchTasks, cancelTask } from '../store/tasksSlice';
import { formatDistanceToNow } from 'date-fns';

function Tasks() {
  const dispatch = useDispatch();
  const { tasks, loading, total, pages } = useSelector((state) => state.tasks);
  const [filters, setFilters] = useState({ status: '', type: '', page: 1 });
  const [expandedTask, setExpandedTask] = useState(null);

  useEffect(() => {
    dispatch(fetchTasks(filters));
  }, [dispatch, filters]);

  const getStatusStyle = (status) => {
    switch (status) {
      case 'completed': return { color: 'var(--neon-green)', icon: '✓' };
      case 'failed': return { color: 'var(--neon-red)', icon: '✗' };
      case 'pending': return { color: 'var(--neon-yellow)', icon: '◐' };
      case 'sent': return { color: 'var(--neon-cyan)', icon: '→' };
      case 'running': return { color: 'var(--neon-orange)', icon: '⟳' };
      case 'cancelled': return { color: 'var(--text-muted)', icon: '⊘' };
      default: return { color: 'var(--text-secondary)', icon: '?' };
    }
  };

  return (
    <>
      <header className="main-header">
        <h1 className="page-title">Tasks</h1>
        <div className="header-actions">
          <span style={{ color: 'var(--text-muted)' }}>
            {total} total tasks
          </span>
        </div>
      </header>

      <div className="main-body">
        {/* Filters */}
        <div className="card" style={{ marginBottom: 'var(--spacing-xl)' }}>
          <div className="card-body" style={{ display: 'flex', gap: 'var(--spacing-md)', alignItems: 'center' }}>
            <select
              className="form-select"
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })}
              style={{ maxWidth: '150px' }}
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="sent">Sent</option>
              <option value="running">Running</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
              <option value="cancelled">Cancelled</option>
            </select>
            
            <select
              className="form-select"
              value={filters.type}
              onChange={(e) => setFilters({ ...filters, type: e.target.value, page: 1 })}
              style={{ maxWidth: '150px' }}
            >
              <option value="">All Types</option>
              <option value="shell">Shell</option>
              <option value="upload">Upload</option>
              <option value="download">Download</option>
              <option value="screenshot">Screenshot</option>
              <option value="sysinfo">System Info</option>
            </select>

            <button 
              className="btn btn-secondary"
              onClick={() => dispatch(fetchTasks(filters))}
            >
              ↻ Refresh
            </button>
          </div>
        </div>

        {/* Tasks Table */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Task History</h3>
          </div>
          <div className="card-body" style={{ padding: 0 }}>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '60px' }}>
                <div className="loader"></div>
              </div>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>Status</th>
                    <th>Task ID</th>
                    <th>Type</th>
                    <th>Command</th>
                    <th>Agent</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {tasks.map((task) => {
                    const statusStyle = getStatusStyle(task.status);
                    return (
                      <React.Fragment key={task.task_id}>
                        <tr 
                          onClick={() => setExpandedTask(expandedTask === task.task_id ? null : task.task_id)}
                          style={{ cursor: 'pointer' }}
                        >
                          <td>
                            <span style={{ color: statusStyle.color }}>
                              {statusStyle.icon} {task.status}
                            </span>
                          </td>
                          <td>
                            <code style={{ color: 'var(--neon-cyan)' }}>
                              {task.task_id?.substring(0, 8)}...
                            </code>
                          </td>
                          <td>{task.task_type}</td>
                          <td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            <code>{task.command || '-'}</code>
                          </td>
                          <td>
                            <code>{task.agent_id}</code>
                          </td>
                          <td>
                            {task.created_at && formatDistanceToNow(new Date(task.created_at), { addSuffix: true })}
                          </td>
                          <td onClick={(e) => e.stopPropagation()}>
                            {(task.status === 'pending' || task.status === 'sent') && (
                              <button
                                className="btn btn-sm btn-danger"
                                onClick={() => dispatch(cancelTask(task.task_id))}
                              >
                                Cancel
                              </button>
                            )}
                          </td>
                        </tr>
                        {expandedTask === task.task_id && (
                          <tr>
                            <td colSpan={7} style={{ background: 'var(--bg-primary)', padding: 'var(--spacing-lg)' }}>
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--spacing-lg)' }}>
                                <div>
                                  <div className="form-label">Full Command</div>
                                  <pre style={{ 
                                    background: 'var(--bg-secondary)', 
                                    padding: 'var(--spacing-md)',
                                    borderRadius: 'var(--radius-sm)',
                                    overflow: 'auto',
                                    maxHeight: '100px'
                                  }}>
                                    {task.command || 'No command'}
                                  </pre>
                                </div>
                                <div>
                                  <div className="form-label">Result</div>
                                  <pre style={{ 
                                    background: 'var(--bg-secondary)', 
                                    padding: 'var(--spacing-md)',
                                    borderRadius: 'var(--radius-sm)',
                                    overflow: 'auto',
                                    maxHeight: '100px',
                                    color: task.status === 'failed' ? 'var(--neon-red)' : 'var(--text-primary)'
                                  }}>
                                    {task.result || task.error_message || 'No result yet'}
                                  </pre>
                                </div>
                              </div>
                              <div style={{ marginTop: 'var(--spacing-md)', color: 'var(--text-muted)', fontSize: '11px' }}>
                                Created: {task.created_at && new Date(task.created_at).toLocaleString()} | 
                                Sent: {task.sent_at ? new Date(task.sent_at).toLocaleString() : 'Not sent'} | 
                                Completed: {task.completed_at ? new Date(task.completed_at).toLocaleString() : 'Not completed'}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            )}
            {tasks.length === 0 && !loading && (
              <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-muted)' }}>
                No tasks found
              </div>
            )}
          </div>

          {/* Pagination */}
          {pages > 1 && (
            <div className="card-body" style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              gap: 'var(--spacing-sm)',
              borderTop: '1px solid var(--border-color)'
            }}>
              {Array.from({ length: Math.min(pages, 10) }, (_, i) => i + 1).map(page => (
                <button
                  key={page}
                  className={`btn btn-sm ${page === filters.page ? 'btn-primary' : 'btn-ghost'}`}
                  onClick={() => setFilters({ ...filters, page })}
                >
                  {page}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default Tasks;
